import { useMemo } from "react";
import { useMultipleAPIs } from "@/services/api/hook";
import { toDateOnlyISO } from "@/utils/datetime";
import { isSelectionSendable } from "./periods";
import {
  COMPARE_MAX_IDS_PER_REQUEST,
  type CompareToSelection,
  type RowComparison
} from "./types";

/**
 * Splits ids into fixed-size blocks aligned to their index in the list.
 *
 * Fixed alignment is the point: the table appends rows as the user scrolls, so
 * a single query keyed on the whole id array would refetch every row on every
 * page. Anchoring each block to its offset leaves already-fetched blocks
 * byte-identical, so React Query serves them from cache and only the new tail
 * goes over the wire.
 */
export const chunkIds = (
  ids: readonly string[],
  chunkSize: number = COMPARE_MAX_IDS_PER_REQUEST
): string[][] => {
  const chunks: string[][] = [];
  // Fall back to the cap rather than trusting the argument, at both bounds. A
  // non-positive size never advances the index, so the loop below would spin
  // forever; `NaN` slips past a `< 1` test entirely (`NaN < 1` is false) and
  // yields one empty chunk, which the endpoint rejects for carrying no ids at
  // all. Above the cap is the mirror failure — it answers a bad argument by
  // breaking the very limit this function exists to enforce, turning a loud
  // hang into a request the API refuses. A fractional size slices at
  // non-integer offsets. Clamping keeps the contract whatever the caller passes.
  const size =
    Number.isFinite(chunkSize) &&
    chunkSize >= 1 &&
    chunkSize <= COMPARE_MAX_IDS_PER_REQUEST
      ? Math.floor(chunkSize)
      : COMPARE_MAX_IDS_PER_REQUEST;
  for (let index = 0; index < ids.length; index += size) {
    chunks.push(ids.slice(index, index + size));
  }
  return chunks;
};

/**
 * What differs between one grain's compare endpoint and another's.
 *
 * Pass a module-level constant, never an inline literal: it lands in a
 * `useMemo` dependency list below, and a fresh object each render would refetch
 * the whole page every time.
 */
export interface ComparisonSpec<TComparison extends RowComparison> {
  /** Route key in `routes.tsx`, e.g. `contracts/compare`. */
  endpoint: string;
  /** Request body field carrying the ids, e.g. `contractIds`. */
  idsField: string;
  /** Response field holding the per-row comparisons, e.g. `contracts`. */
  rowsField: string;
  /** The row's own identifier, however this grain names it. */
  readRowId: (row: TComparison) => string | undefined;
  /**
   * Response field listing ids the endpoint could not resolve. Only the
   * company grain reports them; the contract grain drops them silently, which
   * is why `notFoundIds` is empty rather than absent for that one.
   */
  notFoundField?: string;
}

export interface ComparisonResult<TComparison extends RowComparison> {
  /** Keyed by row id. Ids the API did not answer for are simply absent. */
  byRowId: Map<string, TComparison>;
  /**
   * Ids the endpoint reported as unresolvable. Distinct from a row that is
   * merely absent from `byRowId`, and distinct again from `existed: false` —
   * the first means the entity is gone, the second that it has no snapshot.
   */
  notFoundIds: string[];
  /** The period start the server actually resolved, for display and tooltips. */
  periodStart: string | null;
  isFetching: boolean;
  isError: boolean;
  error: string | null;
}

const EMPTY_RESULT: ComparisonResult<RowComparison> = {
  byRowId: new Map(),
  notFoundIds: [],
  periodStart: null,
  isFetching: false,
  isError: false,
  error: null
};

/**
 * Fetches per-row movement for the rows currently on screen.
 *
 * The compare endpoints are companions to each table's own paginated read
 * rather than part of it, so results are merged into cells client-side and the
 * Δ columns cannot be server-sorted.
 *
 * Grain-agnostic because the two endpoints differ only in their route and
 * their field names — the chunking, the period handling and the aggregate are
 * identical, and duplicating them once per table is how they drift.
 */
export const useComparison = <TComparison extends RowComparison>(
  ids: readonly string[],
  selection: CompareToSelection | undefined,
  enabled: boolean,
  spec: ComparisonSpec<TComparison>
): ComparisonResult<TComparison> => {
  const sendable = isSelectionSendable(selection);
  const isActive = enabled && sendable && ids.length > 0;

  // Serialized so the chunk memo below is not invalidated by a new array
  // identity holding the same ids, which happens on every table re-render.
  const idsKey = ids.join(",");

  const chunks = useMemo(
    () => (isActive ? chunkIds(ids) : []),
    // Keyed on `idsKey` for the reason given at its declaration.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [idsKey, isActive]
  );

  const customStart =
    sendable && selection.period === "custom" && selection.customStart
      ? toDateOnlyISO(selection.customStart)
      : undefined;

  const configs = useMemo(
    () =>
      chunks.map((chunk) => ({
        endpoint: spec.endpoint,
        options: {
          method: "POST",
          data: {
            [spec.idsField]: chunk,
            period: selection!.period,
            ...(customStart ? { customStart } : {})
          }
        },
        queryOptions: { enabled: true }
      })),
    [chunks, selection, customStart, spec]
  );

  const results = useMultipleAPIs(configs);

  // `useQueries` hands back a fresh array on every render, so memoizing the
  // aggregate on it would rebuild `byRowId` each time — and each table's column
  // memo depends on that map, so every render would rebuild every column. Keyed
  // instead on the query state that can actually change the result, which
  // leaves the map referentially stable while nothing has moved.
  const resultsSignature = results
    .map(
      (result) =>
        `${result.dataUpdatedAt}:${result.errorUpdatedAt}:${
          result.isFetching ? 1 : 0
        }`
    )
    .join("|");

  return useMemo(() => {
    if (!isActive) return EMPTY_RESULT as ComparisonResult<TComparison>;

    const byRowId = new Map<string, TComparison>();
    const notFoundIds: string[] = [];
    let periodStart: string | null = null;
    let isFetching = false;
    let isError = false;
    let error: string | null = null;

    results.forEach((result) => {
      if (result.isFetching) isFetching = true;
      if (result.isError) {
        isError = true;
        // A client-visible error string has to be non-null whenever isError is
        // set, or the table cannot tell "failed" from "still loading".
        error = error ?? result.error?.message ?? "compareToFailed";
      }
      const page = result.data as Record<string, unknown> | undefined;
      // `useMultipleAPIs` types its data `unknown`, so this cast is the only
      // thing asserting the shape. A truthy non-array rows field would throw
      // out of a render-path useMemo, which no caller catches.
      const rows = page?.[spec.rowsField];
      if (!Array.isArray(rows)) return;
      periodStart =
        periodStart ?? (page?.periodStart as string | undefined) ?? null;
      // Ids the API has no row for are omitted from the array rather than
      // returned as nulls, so this is keyed by the row's id and never zipped.
      rows.forEach((row: TComparison) => {
        const rowId = row && spec.readRowId(row);
        // Two separate reasons to drop a row, both of which would otherwise
        // surface far from here. Without an id it lands under `undefined` and
        // corrupts every lookup in the chunk. Without a `metrics` object the
        // cell renderers index straight into it — `comparison?.metrics[key]`
        // guards the comparison being absent, not `metrics` being absent — and
        // the TypeError escapes a render-path useMemo that no caller catches.
        // An absent row renders as "no comparison", which is the honest result
        // for a payload this malformed.
        // `typeof` alone is not enough: it reports "object" for an array and
        // for null. An array slips through as a stored row whose every metric
        // lookup yields undefined, so the row renders as "nothing moved" —
        // indistinguishable from a real answer, which is worse than dropping it.
        const hasMetrics =
          !!row &&
          typeof row.metrics === "object" &&
          row.metrics !== null &&
          !Array.isArray(row.metrics);
        if (rowId && hasMetrics) byRowId.set(rowId, row);
      });
      if (spec.notFoundField) {
        const reported = page?.[spec.notFoundField];
        // Guarded rather than trusted. The company endpoint declares this field
        // with a list default and sets no response exclusions, so today it is
        // always present (empty when there is nothing to report) — but a grain
        // that omits it, or any non-array, must degrade to "nothing reported"
        // rather than throwing out of a render-path useMemo.
        if (Array.isArray(reported)) {
          reported.forEach((id) => {
            if (typeof id === "string" && id) notFoundIds.push(id);
          });
        }
      }
    });

    return { byRowId, notFoundIds, periodStart, isFetching, isError, error };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultsSignature, isActive, spec]);
};
